import { useState, useEffect, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useDragControls,
} from "framer-motion";
import usePopups from "../../api/popups";
import useItems from "../../api/items";
import { useTheme } from "../../context/ThemeContext";
import { Switch } from "../ui/switch";
import {
  MessageSquare,
  Plus,
  Pencil,
  Trash2,
  Eye,
  X,
  Upload,
  ImageIcon,
  Sparkles,
  Loader2,
} from "lucide-react";

const S3_BASE_URL =
  process.env.REACT_APP_S3_BASE_URL ||
  "https://zichtbaar-images.s3.eu-north-1.amazonaws.com";

const resolveImageUrl = (imageKey) => {
  if (!imageKey) return null;
  if (imageKey.startsWith("http")) return imageKey;
  return `${S3_BASE_URL}/${imageKey}`;
};

// ── Multilingual popup text helpers ──
// title/message/button_text are stored as plain backend strings. To support
// multiple languages without a schema change, we encode them as a JSON object
// { nl, fr, en } when more than one language is filled in. Plain strings keep
// working (treated as the NL value), so it's fully backward compatible.
const POPUP_LANGS = [
  { code: "nl", label: "NL" },
  { code: "fr", label: "FR" },
  { code: "en", label: "EN" },
];

const parseML = (value) => {
  if (value && typeof value === "object") {
    return { nl: value.nl || "", fr: value.fr || "", en: value.en || "" };
  }
  if (typeof value === "string") {
    const s = value.trim();
    if (s.startsWith("{") && s.includes('"nl"')) {
      try {
        const o = JSON.parse(s);
        if (o && typeof o === "object") {
          return { nl: o.nl || "", fr: o.fr || "", en: o.en || "" };
        }
      } catch {
        /* not JSON — treat as plain text below */
      }
    }
    return { nl: value, fr: "", en: "" };
  }
  return { nl: "", fr: "", en: "" };
};

// Resolve a (possibly multilingual) value to a single string, with fallback.
const pickML = (value, lang = "nl") => {
  const o = parseML(value);
  return o[lang] || o.nl || o.fr || o.en || "";
};

// Encode an { nl, fr, en } object back to what we store on the backend.
const encodeML = (obj) => {
  const o = { nl: obj?.nl || "", fr: obj?.fr || "", en: obj?.en || "" };
  return o.fr || o.en ? JSON.stringify(o) : o.nl;
};

// True when a stored popup already has non-NL content (so we open it in
// multilingual mode automatically).
const popupHasTranslations = (popup) =>
  ["title", "message", "button_text"].some((f) => {
    const o = parseML(popup?.[f]);
    return o.fr || o.en;
  });

// Free Google translate endpoint (same approach as the items auto-translate).
const translateText = async (text, sourceLang, targetLang) => {
  if (!text) return "";
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${targetLang}&dt=t&q=${encodeURIComponent(
      text,
    )}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data && Array.isArray(data[0])) {
      return data[0].map((seg) => (seg && seg[0] ? seg[0] : "")).join("");
    }
    return text;
  } catch (error) {
    console.error("Translation API error:", error);
    return text;
  }
};

const defaultPopup = {
  title: { nl: "", fr: "", en: "" },
  message: { nl: "", fr: "", en: "" },
  button_text: { nl: "Sluiten", fr: "", en: "" },
  bg_color: "#ffffff",
  text_color: "#000000",
  button_color: "#000000",
  overlay_opacity: 0.5,
  is_active: false,
  image_key: "",
};

// Fresh deep copy of the default (the nested language objects must not be shared).
const freshPopup = () => ({
  ...defaultPopup,
  title: { ...defaultPopup.title },
  message: { ...defaultPopup.message },
  button_text: { ...defaultPopup.button_text },
});

// Build editor form state from a stored popup (decodes the multilingual fields).
const popupToForm = (popup) => ({
  title: parseML(popup.title),
  message: parseML(popup.message),
  button_text: (() => {
    const m = parseML(popup.button_text);
    if (!m.nl && !m.fr && !m.en) m.nl = "Sluiten";
    return m;
  })(),
  bg_color: popup.bg_color || "#ffffff",
  text_color: popup.text_color || "#000000",
  button_color: popup.button_color || "#000000",
  overlay_opacity: popup.overlay_opacity ?? 0.5,
  is_active: Boolean(popup.is_active),
  image_key: popup.image_key || "",
});

const PopupsManager = ({ targetCompanyId }) => {
  const {
    getAllPopups,
    createPopup,
    updatePopup,
    togglePopup,
    deletePopup,
    getPopupsByCompanyId,
    createPopupForCompany,
    updatePopupForCompany,
    togglePopupForCompany,
    deletePopupForCompany,
  } = usePopups();
  const { uploadMedia } = useItems();

  const { theme } = useTheme();
  const isDark = theme === "dark";

  const isAdmin = !!targetCompanyId;

  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [formData, setFormData] = useState(() => freshPopup());
  // Language currently being edited / previewed for the multilingual text fields.
  const [editLang, setEditLang] = useState("nl");
  const [previewLang, setPreviewLang] = useState("nl");
  // Multilingual mode is opt-in per popup (like the items translations toggle).
  const [useTranslations, setUseTranslations] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchPopups();
  }, [targetCompanyId]);

  const fetchPopups = async () => {
    try {
      setLoading(true);
      const data = isAdmin
        ? await getPopupsByCompanyId(targetCompanyId)
        : await getAllPopups();
      setPopups(data || []);
    } catch (error) {
      console.error("Error fetching popups:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPopup(null);
    setEditLang("nl");
    setUseTranslations(false);
    setFormData(freshPopup());
    setDialogOpen(true);
  };

  const handleEdit = (popup) => {
    setEditingPopup(popup);
    setEditLang("nl");
    // Open in multilingual mode automatically if the popup already has FR/EN.
    setUseTranslations(popupHasTranslations(popup));
    setFormData(popupToForm(popup));
    setDialogOpen(true);
  };

  // Toggle multilingual mode on/off (mirrors the items translations switch).
  const handleToggleTranslations = (enabled) => {
    setUseTranslations(enabled);
    if (!enabled) setEditLang("nl"); // collapse back to the single (NL) value
  };

  // Auto-translate NL → FR/EN for all three text fields.
  const handleAutoTranslate = async () => {
    setIsTranslating(true);
    try {
      const source = "nl";
      const targets = POPUP_LANGS.map((l) => l.code).filter((c) => c !== source);
      const fields = ["title", "message", "button_text"];
      const updates = {};
      for (const field of fields) {
        const obj = parseML(formData[field]);
        const srcText = obj[source];
        const next = { ...obj };
        if (srcText) {
          for (const target of targets) {
            next[target] = await translateText(srcText, source, target);
          }
        }
        updates[field] = next;
      }
      setFormData((prev) => ({ ...prev, ...updates }));
    } catch (error) {
      console.error("Error auto-translating popup:", error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Encode the text fields. When translations are off we store plain NL
      // strings (so a single-language popup never becomes a JSON blob).
      const encodeField = (obj) =>
        useTranslations ? encodeML(obj) : obj?.nl || "";
      const payload = {
        ...formData,
        title: encodeField(formData.title),
        message: encodeField(formData.message),
        button_text: encodeField(formData.button_text),
      };
      if (editingPopup) {
        if (isAdmin) {
          await updatePopupForCompany(targetCompanyId, editingPopup.id, payload);
        } else {
          await updatePopup(editingPopup.id, payload);
        }
      } else {
        if (isAdmin) {
          await createPopupForCompany(targetCompanyId, payload);
        } else {
          await createPopup(payload);
        }
      }
      setDialogOpen(false);
      await fetchPopups();
    } catch (error) {
      console.error("Error saving popup:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (popup) => {
    try {
      if (isAdmin) {
        await togglePopupForCompany(targetCompanyId, popup.id);
      } else {
        await togglePopup(popup.id);
      }
      await fetchPopups();
    } catch (error) {
      console.error("Error toggling popup:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      if (isAdmin) {
        await deletePopupForCompany(targetCompanyId, id);
      } else {
        await deletePopup(id);
      }
      setDeleteConfirm(null);
      await fetchPopups();
    } catch (error) {
      console.error("Error deleting popup:", error);
    }
  };

  const handlePreview = (popup) => {
    setPreviewLang("nl");
    setFormData(popupToForm(popup));
    setPreviewOpen(true);
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Update one language of a multilingual text field (title/message/button_text).
  const updateMLField = (field, lang, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { ...parseML(prev[field]), [lang]: value },
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      setUploadProgress(0);
      const result = await uploadMedia(file, (progress) => {
        setUploadProgress(progress);
      });
      if (result?.data?.mediaKey) {
        updateField("image_key", result.data.mediaKey);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = () => {
    updateField("image_key", "");
  };

  // Language switcher for the multilingual text fields (NL / FR / EN).
  const LangTabs = ({ value, onChange }) => (
    <div className="flex items-center gap-1">
      {POPUP_LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => onChange(l.code)}
          className={`px-2.5 h-6 rounded-md text-[11px] font-semibold transition-colors ${
            value === l.code
              ? isDark
                ? "bg-white text-black"
                : "bg-gray-900 text-white"
              : isDark
                ? "bg-white/[0.04] text-gray-400 hover:bg-white/[0.08]"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );

  // Translations toggle + (when on) language tabs and the auto-translate button.
  const MLControls = () => (
    <div className="mb-1">
      <div
        className={`flex items-center justify-between p-2.5 rounded-lg ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
      >
        <div>
          <p
            className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
          >
            Meerdere talen
          </p>
          <p
            className={`text-[11px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
          >
            Toon de pop-up in NL / FR / EN
          </p>
        </div>
        <Switch
          checked={useTranslations}
          onCheckedChange={handleToggleTranslations}
        />
      </div>

      {useTranslations && (
        <div className="flex items-center justify-between gap-2 mt-2.5">
          <LangTabs value={editLang} onChange={setEditLang} />
          <button
            type="button"
            onClick={handleAutoTranslate}
            disabled={isTranslating}
            className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-semibold transition-colors disabled:opacity-50 ${isDark ? "bg-white/[0.06] text-gray-200 hover:bg-white/[0.1]" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            title="Vertaal automatisch van Nederlands naar Frans en Engels"
          >
            {isTranslating ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Sparkles className="w-3 h-3" />
            )}
            Auto-vertaal
          </button>
        </div>
      )}
    </div>
  );

  // ===== MOBILE BOTTOM SHEET FOR POPUP DIALOG =====
  const MobilePopupSheet = ({
    onClose,
    isDark,
    editingPopup,
    formData,
    updateField,
    handleSave,
    saving,
    fileInputRef,
    handleImageUpload,
    handleRemoveImage,
    uploading,
    uploadProgress,
    PopupPreview,
  }) => {
    const sheetY = useMotionValue(0);
    const sheetRef = useRef(null);
    const contentRef = useRef(null);
    const dragControls = useDragControls();
    const isScrolledToTop = useRef(true);
    const isDragging = useRef(false);

    useEffect(() => {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }, []);

    const handleContentScroll = (e) => {
      isScrolledToTop.current = e.target.scrollTop <= 0;
    };

    const handleContentPointerDown = (e) => {
      if (isScrolledToTop.current) {
        isDragging.current = true;
        dragControls.start(e);
      }
    };

    return (
      <>
        {/* Sheet */}
        <motion.div
          ref={sheetRef}
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 35, stiffness: 400 }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          dragListener={false}
          dragControls={dragControls}
          onDragEnd={(_, info) => {
            if (info.offset.y > 80 || info.velocity.y > 500) {
              onClose();
            }
          }}
          className={`absolute bottom-0 left-0 right-0 rounded-t-2xl flex flex-col ${isDark ? "bg-[#141414] border-t border-white/[0.08]" : "bg-white border-t border-gray-200"}`}
          style={{ y: sheetY, maxHeight: "92vh" }}
        >
          {/* Drag handle */}
          <div
            onPointerDown={(e) => dragControls.start(e)}
            className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none shrink-0"
            style={{ touchAction: "none" }}
          >
            <div
              className={`w-10 h-1 rounded-full ${isDark ? "bg-white/20" : "bg-gray-300"}`}
            />
          </div>

          {/* Sheet header */}
          <div
            className={`px-5 pb-3 border-b shrink-0 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1 mr-3">
                <h3
                  className={`text-[15px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {editingPopup ? "Pop-up bewerken" : "Nieuwe pop-up"}
                </h3>
                <p
                  className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Pas de inhoud en stijl aan
                </p>
              </div>
              <button
                onClick={onClose}
                className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center transition-colors ${isDark ? "hover:bg-white/[0.06] text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Sheet content */}
          <div
            ref={contentRef}
            onScroll={handleContentScroll}
            onPointerDown={handleContentPointerDown}
            className="overflow-y-auto overscroll-contain p-5 flex-1"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: isScrolledToTop.current ? "none" : "pan-y",
            }}
          >
            <div className="space-y-4">
              {/* Image upload */}
              <div>
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Afbeelding (optioneel)
                </label>
                {formData.image_key ? (
                  <div
                    className={`relative rounded-xl overflow-hidden ${isDark ? "border border-white/[0.06] bg-white/[0.02]" : "border border-gray-200 bg-gray-50"}`}
                  >
                    <img
                      src={resolveImageUrl(formData.image_key)}
                      alt="Popup afbeelding"
                      className="w-full object-contain"
                      style={{ maxHeight: 160, minHeight: 60 }}
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 w-7 h-7 bg-red-500/90 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${isDark ? "border-white/[0.08] hover:border-white/[0.15]" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    {uploading ? (
                      <div className="space-y-2">
                        <div
                          className={`w-6 h-6 rounded-full border-2 animate-spin mx-auto ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
                        />
                        <p
                          className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Uploaden... {uploadProgress}%
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload
                          className={`w-5 h-5 mx-auto mb-2 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        />
                        <p
                          className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
                        >
                          Klik om een afbeelding te uploaden
                        </p>
                      </>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
              </div>

              <MLControls />

              <div>
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Titel
                </label>
                <input
                  value={pickML(formData.title, editLang)}
                  onChange={(e) => updateMLField("title", editLang, e.target.value)}
                  placeholder="bv. Even geduld..."
                  className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                />
              </div>

              <div>
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Bericht
                </label>
                <textarea
                  value={pickML(formData.message, editLang)}
                  onChange={(e) =>
                    updateMLField("message", editLang, e.target.value)
                  }
                  placeholder="bv. Onze winkel is tijdelijk gesloten."
                  rows={3}
                  className={`w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                />
              </div>

              <div>
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Knoptekst
                </label>
                <input
                  value={pickML(formData.button_text, editLang)}
                  onChange={(e) =>
                    updateMLField("button_text", editLang, e.target.value)
                  }
                  placeholder="Sluiten"
                  className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Achtergrond", field: "bg_color" },
                  { label: "Tekst", field: "text_color" },
                  { label: "Knop", field: "button_color" },
                ].map(({ label, field }) => (
                  <div key={field}>
                    <label
                      className={`text-[10px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      {label}
                    </label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="color"
                        value={formData[field]}
                        onChange={(e) => updateField(field, e.target.value)}
                        className="w-7 h-7 rounded border cursor-pointer"
                      />
                      <input
                        value={formData[field]}
                        onChange={(e) => updateField(field, e.target.value)}
                        className={`w-full h-7 px-1.5 rounded-lg text-[10px] font-mono outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 focus:border-gray-300"}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Overlay: {Math.round((formData.overlay_opacity ?? 0.5) * 100)}
                  %
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={formData.overlay_opacity ?? 0.5}
                  onChange={(e) =>
                    updateField("overlay_opacity", parseFloat(e.target.value))
                  }
                  className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${isDark ? "bg-white/[0.06] accent-white" : "bg-gray-200 accent-black"}`}
                />
              </div>

              <div
                className={`flex items-center justify-between p-3 rounded-lg ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
              >
                <label
                  className={`text-[13px] font-medium cursor-pointer ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Direct activeren
                </label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    updateField("is_active", checked)
                  }
                />
              </div>

              {/* Mini preview */}
              <div>
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Preview
                </label>
                <PopupPreview data={formData} small lang={editLang} />
              </div>
            </div>
          </div>

          {/* Fixed bottom actions */}
          <div
            className={`px-5 py-3 border-t shrink-0 flex gap-2 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <button
              onClick={onClose}
              className={`flex-1 h-10 rounded-xl text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              Annuleren
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex-1 h-10 rounded-xl text-[13px] font-medium transition-colors disabled:opacity-40 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            >
              {saving ? "Opslaan..." : editingPopup ? "Opslaan" : "Aanmaken"}
            </button>
          </div>
        </motion.div>
      </>
    );
  };

  // ===== LIVE PREVIEW COMPONENT =====
  const PopupPreview = ({ data, small = false, lang = "nl" }) => {
    const imageUrl = resolveImageUrl(data.image_key);
    const title = pickML(data.title, lang);
    const message = pickML(data.message, lang);
    const buttonText = pickML(data.button_text, lang) || "Sluiten";
    return (
      <div
        className={`relative flex items-center justify-center rounded-xl overflow-hidden ${small ? "" : "border-2 border-gray-200"}`}
        style={{
          minHeight: small ? 120 : 320,
          height: small ? 140 : "auto",
          background: "#1a1a2e",
        }}
      >
        {/* Simulated page content behind overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-4 pointer-events-none select-none">
          {/* Fake browser bar */}
          {!small && (
            <div className="absolute top-0 left-0 right-0 h-7 bg-gray-800 flex items-center px-3 gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400/60"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-400/60"></div>
              <div className="ml-3 flex-1 h-3.5 bg-gray-700 rounded-full max-w-[160px]"></div>
            </div>
          )}
          <div
            className={`text-center ${small ? "space-y-1" : "space-y-2 mt-7"}`}
          >
            <div
              className={`${small ? "w-16" : "w-32"} h-1.5 bg-white/10 rounded mx-auto`}
            ></div>
            <div
              className={`${small ? "w-12" : "w-24"} h-1.5 bg-white/8 rounded mx-auto`}
            ></div>
            <div
              className={`${small ? "w-10" : "w-20"} h-1.5 bg-white/5 rounded mx-auto`}
            ></div>
          </div>
        </div>

        {/* Overlay */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            backgroundColor: `rgba(0, 0, 0, ${data.overlay_opacity ?? 0.5})`,
            backdropFilter: "blur(4px)",
          }}
        >
          {/* Popup box */}
          <div
            className="rounded-2xl text-center overflow-hidden"
            style={{
              backgroundColor: data.bg_color || "#ffffff",
              maxWidth: small ? 180 : 360,
              width: small ? "85%" : "88%",
              boxShadow:
                "0 12px 48px 0 rgba(0,0,0,0.25), 0 2px 8px 0 rgba(0,0,0,0.1)",
            }}
          >
            {/* Close btn in preview */}
            {!small && (
              <div className="relative">
                <div
                  className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full text-white text-[10px] z-10"
                  style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
                >
                  ✕
                </div>
              </div>
            )}
            {imageUrl && (
              <div
                className="w-full"
                style={{ backgroundColor: data.bg_color || "#ffffff" }}
              >
                <img
                  src={imageUrl}
                  alt=""
                  className="w-full object-contain"
                  style={{
                    maxHeight: small ? 44 : 150,
                    minHeight: small ? 28 : 60,
                    display: "block",
                    margin: "0 auto",
                  }}
                />
              </div>
            )}
            <div style={{ padding: small ? "10px 14px" : "24px 28px" }}>
              {title && (
                <h3
                  className="font-bold leading-tight"
                  style={{
                    color: data.text_color || "#000000",
                    fontSize: small ? 10 : 18,
                    marginBottom: small ? 3 : 10,
                  }}
                >
                  {title}
                </h3>
              )}
              {message && (
                <p
                  className="leading-snug"
                  style={{
                    color: data.text_color || "#000000",
                    opacity: 0.75,
                    fontSize: small ? 7.5 : 13,
                    marginBottom: small ? 6 : 16,
                    whiteSpace: "pre-wrap",
                    display: "-webkit-box",
                    WebkitLineClamp: small ? 2 : 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {message}
                </p>
              )}
              <button
                className="rounded-lg font-semibold transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: data.button_color || "#000000",
                  color: data.bg_color || "#ffffff",
                  padding: small ? "3px 10px" : "10px 28px",
                  fontSize: small ? 7.5 : 13,
                  border: "none",
                  cursor: "default",
                  borderRadius: small ? 6 : 10,
                }}
              >
                {buttonText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div
        className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-xl p-5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        <div>
          <h2
            className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Pop-ups
          </h2>
          <p
            className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Beheer pop-ups die verschijnen wanneer bezoekers je site openen
          </p>
        </div>
        <button
          onClick={handleCreate}
          className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
        >
          <Plus className="h-3.5 w-3.5" />
          Nieuwe pop-up
        </button>
      </div>

      {/* Active popup indicator */}
      {popups.some((p) => p.is_active) && (
        <div
          className={`flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}
        >
          <div
            className={`w-2.5 h-2.5 rounded-full animate-pulse ${isDark ? "bg-emerald-400" : "bg-emerald-500"}`}
          />
          <span className="font-medium">
            Er is momenteel een actieve pop-up zichtbaar op je site
          </span>
        </div>
      )}

      {/* Popups list */}
      <div
        className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        <div
          className={`px-5 py-4 ${isDark ? "border-b border-white/[0.04]" : "border-b border-gray-100"}`}
        >
          <h3
            className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Alle pop-ups
          </h3>
          <p
            className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            {popups.length} pop-up{popups.length !== 1 ? "s" : ""} aangemaakt
          </p>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex justify-center py-12">
              <div
                className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
              />
            </div>
          ) : popups.length === 0 ? (
            <div className="text-center py-12">
              <div
                className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
              >
                <MessageSquare
                  className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                />
              </div>
              <h3
                className={`text-[14px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-900"}`}
              >
                Nog geen pop-ups
              </h3>
              <p
                className={`text-[13px] mb-5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
              >
                Maak een pop-up aan om bezoekers te informeren wanneer ze je
                site openen
              </p>
              <button
                onClick={handleCreate}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                <Plus className="h-3.5 w-3.5" />
                Eerste pop-up aanmaken
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Desktop table */}
              <div className="hidden md:block">
                {/* Header */}
                <div
                  className={`grid grid-cols-[160px_1fr_90px_80px_100px] gap-4 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-600 border-b border-white/[0.04]" : "text-gray-500 border-b border-gray-100"}`}
                >
                  <div>Preview</div>
                  <div>Titel</div>
                  <div>Status</div>
                  <div className="text-center">Actief</div>
                  <div className="text-right">Acties</div>
                </div>
                {/* Rows */}
                {popups.map((popup, i) => (
                  <div
                    key={popup.id}
                    className={`grid grid-cols-[160px_1fr_90px_80px_100px] gap-4 items-center px-4 py-3 ${i !== popups.length - 1 ? (isDark ? "border-b border-white/[0.04]" : "border-b border-gray-100") : ""} ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50/50"} transition-colors`}
                  >
                    <div>
                      <PopupPreview data={popup} small />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {pickML(popup.title) || "(Geen titel)"}
                      </p>
                      <p
                        className={`text-[12px] line-clamp-2 mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                      >
                        {pickML(popup.message) || "(Geen bericht)"}
                      </p>
                      {popup.image_key && (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium mt-1 ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500"}`}
                        >
                          <ImageIcon className="w-2.5 h-2.5" />
                          Afbeelding
                        </span>
                      )}
                    </div>
                    <div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${popup.is_active ? (isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700") : isDark ? "bg-white/[0.06] text-gray-500" : "bg-gray-100 text-gray-600"}`}
                      >
                        {popup.is_active ? "Actief" : "Inactief"}
                      </span>
                    </div>
                    <div className="text-center">
                      <Switch
                        checked={popup.is_active}
                        onCheckedChange={() => handleToggle(popup)}
                      />
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      {[
                        { icon: Eye, onClick: () => handlePreview(popup) },
                        { icon: Pencil, onClick: () => handleEdit(popup) },
                        {
                          icon: Trash2,
                          onClick: () => setDeleteConfirm(popup.id),
                          danger: true,
                        },
                      ].map(({ icon: Icon, onClick, danger }, idx) => (
                        <button
                          key={idx}
                          onClick={onClick}
                          className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${danger ? (isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50") : isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile popup cards - separate blocks */}
      {!loading && popups.length > 0 && (
        <div className="md:hidden space-y-3 -mt-2">
          {popups.map((popup, i) => (
            <motion.div
              key={popup.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
              className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
            >
              <div className="p-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {pickML(popup.title) || "(Geen titel)"}
                      </p>
                      <span
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-md flex-shrink-0 ${popup.is_active ? (isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700") : isDark ? "bg-white/[0.06] text-gray-500" : "bg-gray-100 text-gray-600"}`}
                      >
                        {popup.is_active ? "Actief" : "Inactief"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Switch
                      checked={popup.is_active}
                      onCheckedChange={() => handleToggle(popup)}
                    />
                    <button
                      onClick={() => handleEdit(popup)}
                      className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 active:bg-white/[0.06]" : "text-gray-500 active:bg-gray-100"}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(popup.id)}
                      className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-600 active:bg-red-500/15" : "text-gray-400 active:bg-red-50"}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <AnimatePresence>
        {dialogOpen && (
          <div
            className={`fixed inset-0 z-50 ${isMobile ? "" : "flex items-center justify-center"}`}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setDialogOpen(false)}
            />
            {isMobile ? (
              <MobilePopupSheet
                onClose={() => setDialogOpen(false)}
                isDark={isDark}
                editingPopup={editingPopup}
                formData={formData}
                updateField={updateField}
                handleSave={handleSave}
                saving={saving}
                fileInputRef={fileInputRef}
                handleImageUpload={handleImageUpload}
                handleRemoveImage={handleRemoveImage}
                uploading={uploading}
                uploadProgress={uploadProgress}
                PopupPreview={PopupPreview}
              />
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.97, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 10 }}
                transition={{ duration: 0.2 }}
                className={`relative rounded-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h2
                      className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {editingPopup ? "Pop-up bewerken" : "Nieuwe pop-up"}
                    </h2>
                    <button
                      onClick={() => setDialogOpen(false)}
                      className={`h-7 w-7 rounded-lg flex items-center justify-center ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p
                    className={`text-[12px] mb-5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Pas de inhoud en stijl van je pop-up aan. Bezoekers zien dit
                    wanneer ze je site openen.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Form fields */}
                    <div className="space-y-4">
                      {/* Image upload */}
                      <div>
                        <label
                          className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Afbeelding (optioneel)
                        </label>
                        {formData.image_key ? (
                          <div
                            className={`relative rounded-xl overflow-hidden ${isDark ? "border border-white/[0.06] bg-white/[0.02]" : "border border-gray-200 bg-gray-50"}`}
                          >
                            <img
                              src={resolveImageUrl(formData.image_key)}
                              alt="Popup afbeelding"
                              className="w-full object-contain"
                              style={{ maxHeight: 200, minHeight: 80 }}
                            />
                            <button
                              onClick={handleRemoveImage}
                              className="absolute top-2 right-2 w-7 h-7 bg-red-500/90 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${isDark ? "border-white/[0.08] hover:border-white/[0.15]" : "border-gray-200 hover:border-gray-300"}`}
                          >
                            {uploading ? (
                              <div className="space-y-2">
                                <div
                                  className={`w-6 h-6 rounded-full border-2 animate-spin mx-auto ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
                                />
                                <p
                                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                >
                                  Uploaden... {uploadProgress}%
                                </p>
                                <div
                                  className={`w-full h-1.5 rounded-full ${isDark ? "bg-white/[0.06]" : "bg-gray-200"}`}
                                >
                                  <div
                                    className={`h-1.5 rounded-full transition-all ${isDark ? "bg-white/40" : "bg-gray-600"}`}
                                    style={{ width: `${uploadProgress}%` }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <>
                                <Upload
                                  className={`w-5 h-5 mx-auto mb-2 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                />
                                <p
                                  className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
                                >
                                  Klik om een afbeelding te uploaden
                                </p>
                              </>
                            )}
                          </div>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/*"
                          className="hidden"
                        />
                      </div>

                      <MLControls />

                      <div>
                        <label
                          className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Titel
                        </label>
                        <input
                          value={pickML(formData.title, editLang)}
                          onChange={(e) =>
                            updateMLField("title", editLang, e.target.value)
                          }
                          placeholder="bv. Even geduld..."
                          className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                        />
                      </div>

                      <div>
                        <label
                          className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Bericht
                        </label>
                        <textarea
                          value={pickML(formData.message, editLang)}
                          onChange={(e) =>
                            updateMLField("message", editLang, e.target.value)
                          }
                          placeholder="bv. Onze winkel is tijdelijk gesloten. We zijn terug op maandag!"
                          rows={4}
                          className={`w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                        />
                      </div>

                      <div>
                        <label
                          className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Knoptekst
                        </label>
                        <input
                          value={pickML(formData.button_text, editLang)}
                          onChange={(e) =>
                            updateMLField(
                              "button_text",
                              editLang,
                              e.target.value,
                            )
                          }
                          placeholder="Sluiten"
                          className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: "Achtergrond", field: "bg_color" },
                          { label: "Tekstkleur", field: "text_color" },
                          { label: "Knopkleur", field: "button_color" },
                        ].map(({ label, field }) => (
                          <div key={field}>
                            <label
                              className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                            >
                              {label}
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={formData[field]}
                                onChange={(e) =>
                                  updateField(field, e.target.value)
                                }
                                className="w-8 h-8 rounded border cursor-pointer"
                              />
                              <input
                                value={formData[field]}
                                onChange={(e) =>
                                  updateField(field, e.target.value)
                                }
                                className={`w-full h-8 px-2 rounded-lg text-[11px] font-mono outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 focus:border-gray-300"}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      <div>
                        <label
                          className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Overlay doorzichtigheid:{" "}
                          {Math.round((formData.overlay_opacity ?? 0.5) * 100)}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={formData.overlay_opacity ?? 0.5}
                          onChange={(e) =>
                            updateField(
                              "overlay_opacity",
                              parseFloat(e.target.value),
                            )
                          }
                          className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${isDark ? "bg-white/[0.06] accent-white" : "bg-gray-200 accent-black"}`}
                        />
                        <div
                          className={`flex justify-between text-[10px] mt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          <span>Helder</span>
                          <span>Donker</span>
                        </div>
                      </div>

                      <div
                        className={`flex items-center justify-between p-3 rounded-lg ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
                      >
                        <label
                          className={`text-[13px] font-medium cursor-pointer ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Direct activeren
                        </label>
                        <Switch
                          checked={formData.is_active}
                          onCheckedChange={(checked) =>
                            updateField("is_active", checked)
                          }
                        />
                      </div>
                    </div>

                    {/* Live preview */}
                    <div>
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Live preview
                      </label>
                      <div className="sticky top-4">
                        <PopupPreview data={formData} lang={editLang} />
                        <p
                          className={`text-[11px] mt-2 text-center ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          Zo ziet de pop-up eruit voor je bezoekers
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`flex justify-end gap-2 mt-6 pt-4 ${isDark ? "border-t border-white/[0.04]" : "border-t border-gray-100"}`}
                  >
                    <button
                      onClick={() => setDialogOpen(false)}
                      className={`h-9 px-4 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      Annuleren
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className={`h-9 px-4 rounded-lg text-[13px] font-medium transition-colors disabled:opacity-40 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                    >
                      {saving
                        ? "Opslaan..."
                        : editingPopup
                          ? "Wijzigingen opslaan"
                          : "Pop-up aanmaken"}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </AnimatePresence>

      {/* Full-screen Preview Dialog */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setPreviewOpen(false)}
          />
          <div
            className={`relative rounded-xl w-full max-w-4xl mx-4 overflow-hidden border-0 ${isDark ? "bg-[#141414]" : "bg-white"}`}
          >
            <div
              className={`flex items-center justify-between px-4 py-2.5 ${isDark ? "border-b border-white/[0.06]" : "border-b border-gray-100"}`}
            >
              <span
                className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Voorbeeld
              </span>
              <LangTabs value={previewLang} onChange={setPreviewLang} />
            </div>
            <div
              className="relative"
              style={{ height: "70vh", background: "#1a1a2e" }}
            >
              {/* Fake browser bar */}
              <div className="absolute top-0 left-0 right-0 h-9 bg-gray-800 flex items-center px-4 gap-2 z-20">
                <div className="w-3 h-3 rounded-full bg-red-400/70"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400/70"></div>
                <div className="w-3 h-3 rounded-full bg-green-400/70"></div>
                <div className="ml-4 flex-1 h-4 bg-gray-700 rounded-full max-w-[240px]"></div>
              </div>

              {/* Simulated page */}
              <div className="absolute inset-0 pt-9 flex items-center justify-center">
                <div className="text-center space-y-4 p-8">
                  <div className="w-48 h-3 bg-white/10 rounded mx-auto"></div>
                  <div className="w-64 h-2.5 bg-white/[0.07] rounded mx-auto"></div>
                  <div className="w-56 h-2.5 bg-white/[0.05] rounded mx-auto"></div>
                  <div className="grid grid-cols-3 gap-4 mt-8">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-20 bg-white/[0.05] rounded-lg"
                      ></div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Overlay */}
              <div
                className="absolute inset-0 pt-9 flex items-center justify-center"
                style={{
                  backgroundColor: `rgba(0, 0, 0, ${formData.overlay_opacity ?? 0.5})`,
                  backdropFilter: "blur(4px)",
                }}
              >
                {/* Popup */}
                <div
                  className="relative rounded-2xl mx-4 text-center overflow-hidden"
                  style={{
                    backgroundColor: formData.bg_color || "#ffffff",
                    maxWidth: "500px",
                    width: "90%",
                    boxShadow:
                      "0 20px 60px 0 rgba(0,0,0,0.35), 0 4px 12px 0 rgba(0,0,0,0.15)",
                  }}
                >
                  <button
                    onClick={() => setPreviewOpen(false)}
                    className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>

                  {formData.image_key && (
                    <div
                      style={{
                        backgroundColor: formData.bg_color || "#ffffff",
                      }}
                    >
                      <img
                        src={resolveImageUrl(formData.image_key)}
                        alt=""
                        className="w-full object-contain"
                        style={{ maxHeight: 280, minHeight: 100 }}
                      />
                    </div>
                  )}
                  <div style={{ padding: "36px 44px" }}>
                    {pickML(formData.title, previewLang) && (
                      <h2
                        className="text-2xl font-bold mb-3"
                        style={{ color: formData.text_color || "#000000" }}
                      >
                        {pickML(formData.title, previewLang)}
                      </h2>
                    )}
                    {pickML(formData.message, previewLang) && (
                      <p
                        className="text-base mb-6"
                        style={{
                          color: formData.text_color || "#000000",
                          opacity: 0.8,
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {pickML(formData.message, previewLang)}
                      </p>
                    )}
                    <button
                      onClick={() => setPreviewOpen(false)}
                      className="rounded-xl font-semibold px-8 py-3"
                      style={{
                        backgroundColor: formData.button_color || "#000000",
                        color: formData.bg_color || "#ffffff",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    >
                      {pickML(formData.button_text, previewLang) || "Sluiten"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          />
          <div
            className={`relative rounded-xl w-full max-w-sm mx-4 p-6 ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
          >
            <h3
              className={`text-[14px] font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Pop-up verwijderen?
            </h3>
            <p
              className={`text-[13px] mb-5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Dit kan niet ongedaan gemaakt worden. De pop-up wordt permanent
              verwijderd.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="h-9 px-4 rounded-lg text-[13px] font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupsManager;
